import { Footer } from "@/components/Footer";
import { Header } from "@/components/Header";
import { PageInfo } from "@/components/PageInfo";
import { Deployment, deploymentFromContext } from "@/domain/deployment";
import { DEFAULT_LANGUAGE } from "@/domain/i18n";
import { MenuItem } from "@/domain/menu";
import { fetchMenuItems } from "@/domain/menu.ssr";
import { NewsItem } from "@/domain/news";
import { OrganizationR } from "@/domain/organization";
import { fetchOrganization, fetchOrganizations, fetchRelatedArticles } from "@/domain/organization.ssr";
import { foldPage_, Page } from "@/domain/page";
import { fetchPage } from "@/domain/page.ssr";
import { GetServerSideProps } from "next";
import dynamic from "next/dynamic";
import React from "react";

const OrganizationDetailTemplate = dynamic(() => import("../../templates/OrganizationDetailTemplate"), {
  ssr: false,
});

interface Props {
  page: Page;
  menuItems: Array<MenuItem>;
  organization: OrganizationR;
  organizations: Array<OrganizationR>;
  articles: Array<NewsItem>;
  deployment: Deployment;
}

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  const deployment = deploymentFromContext(ctx);

  const locale = ctx.locale ?? DEFAULT_LANGUAGE;
  const id = typeof ctx.params?.id === "string" ? ctx.params.id : "NOID";
  const page_id = "atlas";

  const [page, organization, menuItems, organizations, articles] = await Promise.all([
    fetchPage(deployment, locale, page_id),
    fetchOrganization(deployment, locale, id),
    fetchMenuItems(deployment, locale),
    fetchOrganizations(deployment, locale),
    fetchRelatedArticles(deployment, locale, id),
  ]);

  if (!page || !organization) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      menuItems,
      organization,
      organizations,
      articles,
      deployment,
    },
  };
};

export default function OrganizationDetailPage(props: Props) {
  const { page, menuItems, organization, organizations, articles, deployment } = props;

  const footerRef = React.useRef<HTMLDivElement>(null);

  return (
    <>
      <PageInfo title={organization.name} deployment={deployment} />
      <div className="layout-sticky__page">
        <header className="site-header site-header--opaque">
          <div className="site-header__content">
            <Header menuItems={menuItems} deployment={deployment} />
          </div>
        </header>
        <section>
          <OrganizationDetailTemplate
            organization={organization}
            organizations={organizations}
            articles={articles}
            deployment={deployment}
          />
        </section>
      </div>

      <div
        className={foldPage_(page, {
          default: () => "",
          about: () => "",
          contact: () => "layout-sticky--slim",
          news: () => "",
          organizations: () => "",
        })}
      >
        <div className="layout-sticky__footer" data-spacer-element="footer" ref={footerRef}>
          <Footer
            slim={foldPage_(page, {
              default: () => false,
              about: () => false,
              contact: () => true,
              news: () => false,
              organizations: () => false,
            })}
          />
        </div>
      </div>
    </>
  );
}
